/**
 * Premium features and subscription types
 */

/**
 * Premium offering identifiers
 */
export enum PremiumOfferingId {
  MONTHLY = '$rc_monthly',  // RevenueCat package identifier from dashboard
  ANNUAL = '$rc_annual',    // RevenueCat package identifier from dashboard
  LIFETIME = 'peak_lifetime', // Keep as is - not used in current setup
}

/**
 * Package type with pricing and details
 */
export interface PremiumPackage {
  identifier: string;
  offeringIdentifier: string;
  title: string;
  description: string;
  price: string;
  priceValue: number;
  currency: string;
  localizedPriceString: string;
  duration?: string;
  introPrice?: string;
}

/**
 * Offering with available packages
 */
export interface PremiumOffering {
  identifier: string;
  serverDescription: string;
  packages: PremiumPackage[];
}

/**
 * Active subscription details
 */
export interface ActiveSubscription {
  identifier: string;
  isActive: boolean;
  willRenew: boolean;
  periodType: 'normal' | 'intro' | 'trial';
  latestPurchaseDate: string;
  originalPurchaseDate: string;
  expirationDate: string | null;
  store: 'app_store' | 'play_store' | 'stripe' | 'amazon' | 'promotional';
  isSandbox: boolean;
  unsubscribeDetectedAt: string | null;
  billingIssueDetectedAt: string | null;
}

/**
 * Premium features access level
 */
export enum PremiumFeature {
  INJURY_TRACKING = 'injury_tracking',
  PHOTO_GALLERY = 'photo_gallery',
  CUSTOM_METRICS = 'custom_metrics',
  AD_FREE = 'ad_free',
  GOALS = 'goals',
  WORKOUT_ROUTINES = 'workout_routines',
  INSIGHTS = 'insights',
  ROUND_TRACKER = 'round_tracker',
  MULTIPLE_MARTIAL_ARTS = 'multiple_martial_arts',
  LEADERBOARDS = 'leaderboards',
}

/**
 * Premium entitlements
 */
export interface PremiumEntitlements {
  isPeakSubscriber: boolean;
  isAdFree: boolean;
  features: PremiumFeature[];
}

/**
 * Peak insights general structure
 */
export interface PeakInsight {
  id: string;
  title: string;
  description: string;
  createDate: string;
  type: 'comparison' | 'milestone' | 'trend' | 'recommendation';
  category: 'training' | 'competition' | 'progress' | 'general';
  priority: number;
  data: any; // The specific data varies by insight type
}

/**
 * Premium store state
 */
export interface PremiumState {
  offerings: PremiumOffering[];
  currentOffering: PremiumOffering | null;
  entitlements: PremiumEntitlements;
  activeSubscriptions: ActiveSubscription[];
  isLoading: boolean;
  restorePurchaseStatus: 'idle' | 'loading' | 'success' | 'error';
  purchaseStatus: 'idle' | 'loading' | 'success' | 'error';
  error: string | null;
  insights: PeakInsight[];
  insightsLoading: boolean;
  insightsError: string | null;
}

/**
 * Promo code status
 */
export interface PromoCodeStatus {
  isValid: boolean;
  message: string;
  duration: string | null;
}